import { Box, BoxProps, Flex } from '@rebass/grid';
import eachDayOfInterval from 'date-fns/eachDayOfInterval';
import eachWeekOfInterval from 'date-fns/eachWeekOfInterval';
import endOfMonth from 'date-fns/endOfMonth';
import endOfWeek from 'date-fns/endOfWeek';
import format from 'date-fns/format';
import isSameDay from 'date-fns/isSameDay';
import isWithinInterval from 'date-fns/isWithinInterval';
// eslint-disable-next-line no-restricted-imports
import type { Locale } from 'date-fns';
import nl from 'date-fns/locale/nl';
import startOfMonth from 'date-fns/startOfMonth';
import startOfWeek from 'date-fns/startOfWeek';
import React, { ComponentType, forwardRef, Ref, useCallback } from 'react';
import styled from 'styled-components';
import { CLASS_PLANNER_MONTH, CLASS_PLANNER_WEEK } from '../../constants/customizationClassNames';
import { getFontCss } from '../../constants/theme';
import Period from '../../entities/Period';
import useWeekdays from '../../hooks/useWeekdays';
import Title from '../primitive/Title';
import PlannerDay, { clickables, ColorVariant, hoverables, PlannerDayProps, PlannerDayVariant } from './PlannerDay';

export type MonthDayProps = Pick<PlannerDayProps, 'variant' | 'label' | 'icon'>;

export type DayEventHandler = (props: { day: Date; variant?: PlannerDayVariant }) => void;

export interface PlannerMonthProps extends BoxProps {
    locale?: Locale;
    date: Date;
    period?: Period;
    isSelecting?: boolean;
    onDayClick?: DayEventHandler;
    onDayMouseEnter?: DayEventHandler;
    onDayMouseLeave?: DayEventHandler;
    loading?: boolean;
    /** This callback should be used to determine what variant and what label to show */
    computeDayProps?: (day: Date) => MonthDayProps;
    periodColor?: ColorVariant;
    defaultFontColor?: ColorVariant;
}

// there's an issue with date-fns sometimes randomly throwing an error when using `eachDayOfInterval` eventhough it's valid.
// still not sure what exactly causes it.
// https://github.com/date-fns/date-fns/issues/1626
const getDaysOfInterval = (interval: Interval) => {
    try {
        return eachDayOfInterval(interval);
    } catch (error) {
        if (isSameDay(interval.start, interval.end)) {
            return [new Date(interval.start)];
        }
        return [];
    }
};

const PlannerMonth = (
    {
        date,
        period,
        locale = nl,
        computeDayProps,
        onDayClick,
        onDayMouseLeave,
        onDayMouseEnter,
        loading = false,
        isSelecting,
        periodColor,
        defaultFontColor,
        ...props
    }: PlannerMonthProps,
    ref: Ref<HTMLDivElement>
) => {
    const start = startOfMonth(date);
    const end = endOfMonth(date);
    const weekOpts = { locale, weekStartsOn: 1 } as const;
    const weekdays = useWeekdays(weekOpts);

    const weeks = eachWeekOfInterval({ start, end }, weekOpts);
    const fallsInMonth = useCallback((monthDay: Date) => isWithinInterval(monthDay, { start, end }), [end, start]);

    return (
        <PlannerContainer {...props} className={`${props.className ?? ''} ${CLASS_PLANNER_MONTH}`} ref={ref}>
            <Flex width={1} justifyContent="center" mb={5}>
                <Title variant="small">{format(date, `MMMM ‘yy`, { locale })}</Title>
            </Flex>
            <PlannerGrid>
                <PlannerWeek>
                    {weekdays.map(weekday => (
                        <PlannerWeekday key={+weekday}>{format(weekday, 'EEEEEE', { locale })}</PlannerWeekday>
                    ))}
                </PlannerWeek>

                {weeks.map(weekStart => {
                    const weekInterval = {
                        start: startOfWeek(new Date(weekStart), weekOpts),
                        end: endOfWeek(new Date(weekStart), weekOpts),
                    };

                    return (
                        <PlannerWeek key={+weekStart}>
                            {getDaysOfInterval(weekInterval).map(weekday => {
                                if (loading) {
                                    return <PlannerDay key={+weekday} variant="loading" />;
                                }
                                if (fallsInMonth(weekday)) {
                                    const dayProps = computeDayProps?.(weekday);
                                    const { variant } = dayProps ?? {};

                                    const callbackVars = {
                                        day: weekday,
                                        variant,
                                    };
                                    return (
                                        <PlannerDay
                                            key={+weekday}
                                            date={weekday}
                                            isSelecting={isSelecting}
                                            onClick={() => {
                                                if (variant && clickables.includes(variant)) {
                                                    onDayClick?.(callbackVars);
                                                }
                                            }}
                                            onMouseEnter={() => {
                                                if (variant && hoverables.includes(variant)) {
                                                    onDayMouseEnter?.(callbackVars);
                                                }
                                            }}
                                            onMouseDown={() => onDayMouseLeave?.(callbackVars)}
                                            {...dayProps}
                                        />
                                    );
                                }
                                // outside the month, a ghost day if you will
                                return <PlannerDay key={+weekday} />;
                            })}
                        </PlannerWeek>
                    );
                })}
            </PlannerGrid>
        </PlannerContainer>
    );
};

export default forwardRef(PlannerMonth) as ComponentType<React.PropsWithChildren<PlannerMonthProps>>;

export const PlannerWeek = styled.div.attrs({ className: CLASS_PLANNER_WEEK })`
    width: 100%;
    display: flex;
    + div {
        margin-top: 0.4rem;
    }
`;

export const PlannerContainer = styled(Box)`
    flex-shrink: 0;
    width: 100%;

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        width: calc(50% - (var(--monthGap) * 1px) / 2);
    }

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.xl}) {
        width: calc(100% / 3 - (var(--monthGap) * 1px * 2) / 3);
    }
`;

const PlannerGrid = styled.div`
    display: flex;
    flex-direction: column;
    font-family: ${({ theme }) => theme.fontFamily.body};
`;

const PlannerWeekday = styled.div`
    width: calc(100% / 7);
    color: ${({ theme }) => theme.colors.neutral['60']};
    font-weight: lighter;
    display: flex;
    justify-content: center;
    height: 2.4rem;
    margin-bottom: ${({ theme }) => theme.spacing['30_Small']};
    ${({ theme }) => getFontCss(theme.fontSizes.body.small)};
    font-weight: 500;

    & + & {
        margin-left: 0.4rem;
    }
`;
